/**
 * Start somebody's onboarding (PRD 8.1): who, where, when, what kind of role
 * -- and who does each department's part, which starts as the site's
 * defaults and can be changed for this one hire. The hiring manager is the
 * one that nearly always changes, so it is asked first.
 *
 * The choices offered are only the ones the template branches on, as in the
 * preview. The worker checks everything again (`checkStart()`).
 *
 * "Several people" starts a cohort: the same day, site, template and owners
 * for every name pasted in, one onboarding each, all or none (Stage 5).
 */

import { useMemo, useState, type FormEvent } from "react";
import {
  EMPLOYEE_TYPES,
  EMPLOYMENT_STATUSES,
  MANAGER_DEPARTMENT,
  departmentsIn,
  offered,
  type Answer,
  type Assignee,
  type CohortResponse,
  type StartResponse,
  testsField,
  type ProfileKey,
} from "@utuwrk/core";
import { sentence } from "../lib/api.js";
import { useOrg } from "../lib/org.js";
import { go } from "../lib/router.js";
import { words } from "../lib/words.js";
import { Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";
import { Questions } from "./Questions.js";
import { Segmented } from "@vcnet/ui";
import { Started } from "./Started.js";

/** The department whose default is least likely to be right for a given hire. */
const HIRING = MANAGER_DEPARTMENT;

function twoWeeksOut(): string {
  return new Date(Date.now() + 14 * 86_400_000).toISOString().slice(0, 10);
}

export function StartOnboarding() {
  const { api, people, departments: listed } = useOrg();
  const { data: setup, error } = useLoad(() => api.setup(), [api]);
  const [templateId, setTemplateId] = useState("");
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [employeeName, setEmployeeName] = useState("");
  const [siteId, setSiteId] = useState("");
  const [employeeType, setEmployeeType] = useState<string>("office");
  const [employmentStatus, setEmploymentStatus] = useState<string>("full-time");
  const [department, setDepartment] = useState("");
  const [anchorDate, setAnchorDate] = useState(twoWeeksOut);
  const [endDate, setEndDate] = useState("");
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [started, setStarted] = useState<StartResponse | null>(null);
  const [several, setSeveral] = useState(false);
  const [namesText, setNamesText] = useState("");
  const [cohort, setCohort] = useState<CohortResponse | null>(null);
  const names = namesText
    .split(/\r?\n/)
    .map((line) => line.split("\t")[0]!.trim())
    .filter(Boolean);

  const sites = useMemo(() => setup?.levels.filter((level) => level.kind === "site") ?? [], [setup]);
  const site = siteId || sites[0]?.id || "";
  // Only what is used at this site; one chosen for another site is not carried over.
  const atSite = setup?.departmentsAt[site] ?? listed;
  const chosenDepartment = atSite.includes(department) ? department : "";
  // The site's own templates first, and each starter none of them replaces (core's offered()).
  const templates = offered(setup?.templates ?? [], "onboarding", site);
  const template = templates.find((candidate) => candidate.id === templateId) ?? templates[0]!;
  const questions = template.questions ?? [];
  const unanswered = questions.some((question) => answers[question.key] === undefined);
  const departments = useMemo(() => {
    const all = departmentsIn(template);
    return [...all.filter((name) => name === HIRING), ...all.filter((name) => name !== HIRING)];
  }, [template]);
  const tests = (field: ProfileKey) => testsField(template, field);
  const active = people.filter((person) => person.active);

  const defaultFor = (name: string): string => setup?.staffing[site]?.[name]?.primary ?? "";
  const holderOf = (name: string): string => chosen[name] ?? defaultFor(name);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    const assignees: Record<string, Assignee> = {};
    for (const name of departments) {
      if (chosen[name] === undefined || chosen[name] === defaultFor(name)) continue;
      assignees[name] = {
        primary: chosen[name] || null,
        backup: setup?.staffing[site]?.[name]?.backup ?? null,
      };
    }
    const common = {
      templateId: template.id,
      answers: Object.fromEntries(questions.map((question) => [question.key, answers[question.key]!])),
      siteId: site,
      employeeType,
      employmentStatus,
      department: chosenDepartment,
      anchorDate,
      endDate: employmentStatus === "temp" && endDate ? endDate : null,
      assignees,
    };
    try {
      if (several) setCohort(await api.startCohort({ start: common, names }));
      else setStarted(await api.start({ ...common, employeeName }));
    } catch (failure) {
      setProblem(sentence(failure));
    } finally {
      setBusy(false);
    }
  }

  if (!setup && !error) return <Loading title="Start an onboarding" />;

  if (cohort) {
    return (
      <section className="page">
        <PageHeader title={`${cohort.started.length} people are on their way`} />
        <Started
          started={cohort}
          lede="Each has an onboarding of their own. The first cards are open, and each owner was told once for all of them."
          openBoard={() => go({ name: "onboardings" })}
          boards={cohort.started}
        />
      </section>
    );
  }

  if (started) {
    return (
      <section className="page">
        <PageHeader title={`${employeeName.trim()} is on their way`} />
        <Started
          started={started}
          lede="The first cards are open and their owners have been told. Everything else opens as the work before it finishes."
          openBoard={() => go({ name: "board", id: started.id })}
        />
      </section>
    );
  }

  return (
    <section className="page">
      <PageHeader
        back={{ to: { name: "onboardings" }, label: "Onboarding" }}
        title="Start an onboarding"
        meta="Every department is told when its part is ready. Nothing goes out until you press Start."
      />
      <form className="panel" onSubmit={submit}>
        <Problem>{error}</Problem>
        {sites.length === 0 && setup ? (
          <p className="notice-line warn">
            There is no site yet. An admin adds one in <Link to={{ name: "settings" }}>Settings</Link>.
          </p>
        ) : null}

        <div className="start-count">
          <Segmented
            label="How many are starting"
            value={several ? "several" : "one"}
            options={[
              { value: "one", label: "One person" },
              { value: "several", label: "Several people" },
            ]}
            onChange={(value) => setSeveral(value === "several")}
            size="sm"
          />
        </div>

        <div className="controls">
          {several ? (
            <label className="field field-wide">
              <span>Who is starting, one to a line</span>
              <textarea
                rows={5}
                value={namesText}
                onChange={(event) => setNamesText(event.target.value)}
                placeholder={"Ana Ruiz\nBen Okoye\nCy Tran"}
              />
            </label>
          ) : (
            <label className="field">
              <span>Who is starting</span>
              <input
                value={employeeName}
                onChange={(event) => setEmployeeName(event.target.value)}
                placeholder="Dana Reyes"
                autoComplete="off"
              />
            </label>
          )}
          <label className="field">
            <span>Start date</span>
            <input
              type="date"
              value={anchorDate}
              onChange={(event) => event.target.value && setAnchorDate(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Site</span>
            <select value={site} onChange={(event) => setSiteId(event.target.value)}>
              {sites.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          {templates.length > 1 ? (
            <label className="field">
              <span>Template</span>
              <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
                {templates.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tests("employeeType") ? (
            <label className="field">
              <span>Kind of role</span>
              <select value={employeeType} onChange={(event) => setEmployeeType(event.target.value)}>
                {EMPLOYEE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {words(type)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tests("employmentStatus") ? (
            <label className="field">
              <span>Status</span>
              <select value={employmentStatus} onChange={(event) => setEmploymentStatus(event.target.value)}>
                {EMPLOYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {words(status)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {tests("employmentStatus") && employmentStatus === "temp" ? (
            <label className="field">
              <span>Contract ends</span>
              <input
                type="date"
                value={endDate}
                min={anchorDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </label>
          ) : null}
          <label className="field">
            <span>Their department</span>
            <select value={chosenDepartment} onChange={(event) => setDepartment(event.target.value)}>
              <option value="">No department</option>
              {atSite.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Questions
          questions={questions}
          answers={answers}
          onChange={(key, answer) => setAnswers((was) => ({ ...was, [key]: answer }))}
        />

        <h2 className="section-title section-gap">Who does each part</h2>
        <p className="muted">
          Filled in from the site's defaults. Change any of them for {several ? "these hires" : "this hire"};
          the defaults stay as they are.
        </p>
        <div className="controls">
          {departments.map((name) => (
            <label key={name} className="field">
              <span>{name === HIRING ? "Hiring manager" : name}</span>
              <select
                value={holderOf(name)}
                onChange={(event) => setChosen({ ...chosen, [name]: event.target.value })}
              >
                <option value="">Nobody: anyone in {name}</option>
                {[
                  ...active.filter((person) => person.department === name),
                  ...active.filter((person) => person.department !== name),
                ].map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name || person.email}
                    {person.department && person.department !== name ? ` (${person.department})` : ""}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>

        <div className="action-row section-gap">
          <button
            className="button"
            type="submit"
            disabled={busy || (several ? names.length < 2 : !employeeName.trim()) || !site || unanswered}
          >
            {several ? `Start ${names.length} onboardings` : "Start"}
          </button>
          <Link to={{ name: "template", id: template.id }} className="button button-quiet">
            Preview the template first
          </Link>
        </div>
        <Problem>{problem}</Problem>
      </form>
    </section>
  );
}
